<template>
  <d-modal @close="handleClose">
    <d-modal-header>
      <d-modal-title>
        {{ $t('price.editPrice') }}
      </d-modal-title>
    </d-modal-header>
    <d-modal-body>
      <d-form>
        <validation-observer ref="shipper-edit-price-form" tag="form">
          <d-row>
            <d-col cols="12" md="8" lg="8">
              <div class="form-group">
                <label for="name">
                  {{ $t('settings.country.country') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="country-id"
                  v-slot="{ errors }">
                  <d-form-select
                    v-model="countryID"
                    :state="errors.length ? 'invalid' : null"
                    :options="countriesOptions" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>

            <d-col cols="12" md="4" lg="4">
              <div class="form-group">
                <label for="name">
                  {{ $t('settings.currency.currency') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="currency-id"
                  v-slot="{ errors }">
                  <d-form-select
                    v-model="currencyID"
                    :state="errors.length ? 'invalid' : null"
                    :options="currenciesOptions" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>
          </d-row>

          <d-row class="ml-auto mr-auto mt-4 mb-4">
            <d-button-group
              class="w-100"
              size="small">
              <d-button
                theme="primary"
                :outline="priceType !== 'flat'"
                @click.prevent="setPriceType('flat')">
                {{ $t('price.priceFlat') }}
              </d-button>
              <d-button
                theme="primary"
                :outline="priceType !== 'box'"
                @click.prevent="setPriceType('box')">
                {{ $t('price.priceBox') }}
              </d-button>
            </d-button-group>
          </d-row>

          <d-row>
            <d-col cols="12" md="6" lg="6" offset="3">
              <div
                v-if="priceType === 'flat'"
                key="flat-price"
                class="form-group">
                <label for="price">
                  {{ $t('price.price') }}:
                </label>

                <validation-provider
                  rules="required|min_value:0"
                  name="price"
                  v-slot="{ errors }">
                  <d-input-group :class="{ 'is-invalid': errors.length }">
                    <input
                      :class="{
                        'form-control': true,
                        'text-right': true,
                        'is-invalid': errors.length
                      }"
                      :invalid="errors.length ? true : null"
                      type="number"
                      v-model="price" />
                    <d-input-group-text slot="append">
                      {{ selectedCurrencyName }}
                    </d-input-group-text>
                  </d-input-group>
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div
                v-if="priceType === 'box'"
                key="pieces-25-price"
                class="form-group">
                <label for="price">
                  {{ $t('price.price25pcs') }}:
                </label>

                <validation-provider
                  rules="required|min_value:0"
                  name="price25pcs"
                  v-slot="{ errors }">
                  <d-input-group :class="{ 'is-invalid': errors.length }">
                    <input
                      :class="{
                        'form-control': true,
                        'text-right': true,
                        'is-invalid': errors.length
                      }"
                      :invalid="errors.length ? true : null"
                      type="number"
                      v-model="pricePcs" />
                    <d-input-group-text slot="append">
                      {{ selectedCurrencyName }}
                    </d-input-group-text>
                  </d-input-group>
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div
                v-if="priceType === 'box'"
                key="first-box-price"
                class="form-group">
                <label for="priceFirstBox">
                  {{ $t('price.priceFirstBox') }}:
                </label>

                <validation-provider
                  rules="required|min_value:0"
                  name="priceFirstBox"
                  v-slot="{ errors }">
                  <d-input-group :class="{ 'is-invalid': errors.length }">
                    <input
                      :class="{
                        'form-control': true,
                        'text-right': true,
                        'is-invalid': errors.length
                      }"
                      :invalid="errors.length ? true : null"
                      type="number"
                      v-model="priceFirstBox" />
                    <d-input-group-text slot="append">
                      {{ selectedCurrencyName }}
                    </d-input-group-text>
                  </d-input-group>
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div
                v-if="priceType === 'box'"
                key="second-box-price"
                class="form-group">
                <label for="priceSecondBox">
                  {{ $t('price.priceSecondBox') }}:
                </label>

                <validation-provider
                  rules="required|min_value:0"
                  name="priceSecondBox"
                  v-slot="{ errors }">
                  <d-input-group :class="{ 'is-invalid': errors.length }">
                    <input
                      :class="{
                        'form-control': true,
                        'text-right': true,
                        'is-invalid': errors.length
                      }"
                      :invalid="errors.length ? true : null"
                      type="number"
                      v-model="priceSecondBox" />
                    <d-input-group-text slot="append">
                      {{ selectedCurrencyName }}
                    </d-input-group-text>
                  </d-input-group>
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div
                v-if="priceType === 'box'"
                key="next-box-price"
                class="form-group">
                <label for="priceNextBox">
                  {{ $t('price.priceNextBox') }}:
                </label>

                <validation-provider
                  rules="required|min_value:0"
                  name="priceNextBox"
                  v-slot="{ errors }">
                  <d-input-group :class="{ 'is-invalid': errors.length }">
                    <input
                      :class="{
                        'form-control': true,
                        'text-right': true,
                        'is-invalid': errors.length
                      }"
                      :invalid="errors.length ? true : null"
                      type="number"
                      v-model="priceNextBox" />
                    <d-input-group-text slot="append">
                      {{ selectedCurrencyName }}
                    </d-input-group-text>
                  </d-input-group>
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>
          </d-row>
        </validation-observer>
      </d-form>
    </d-modal-body>
    <d-modal-footer>
      <d-button
        class="ml-0"
        theme="danger"
        @click.prevent="cancel">
        {{ $t('ui.cancel') }}
      </d-button>
      <d-button
        class="ml-auto"
        theme="success"
        @click.prevent="validateForm">
        {{ $t('ui.saveChanges') }}
      </d-button>
    </d-modal-footer>
  </d-modal>
</template>

<script>
import CommonGetters from './../../utils/CommonGetters.js';
import FormUtils from './../../utils/FormUtils.js';
import Vue from 'vue';

export default {
  name: 'shipper-price-edit-popup',
  props: {
    shipperID: {
      default: 0,
      type: Number
    },
    priceData: {
      default: () => ({}),
      type: Object
    },
    currencies: {
      default: () => ([]),
      type: Array
    },
    countries: {
      default: () => ([]),
      type: Array
    }
  },
  computed: {
    countriesOptions () {
      return CommonGetters.getCountriesForSelect(this.countries);
    },
    currenciesOptions () {
      return CommonGetters.getCurrenciesForSelect(this.currencies);
    },
    selectedCurrencyName () {
      if (this.currencies.length) {
        let selectedCurrency = this.currencies.filter(currency => currency.id === this.currencyID);

        if (selectedCurrency && selectedCurrency[0]) {
          return selectedCurrency[0].code;
        }
      }

      return '';
    }
  },
  data () {
    return {
      id: '',
      price: '',
      pricePcs: '',
      priceFirstBox: '',
      priceSecondBox: '',
      priceNextBox: '',
      priceType: 'flat',
      currencyID: 0,
      countryID: 0
    };
  },
  mounted () {
    this.id = this.priceData.id;
    this.price = this.priceData.price;
    this.pricePcs = this.priceData.price_pcs;
    this.priceFirstBox = this.priceData.price_first_box;
    this.priceSecondBox = this.priceData.price_second_box;
    this.priceNextBox = this.priceData.price_next_box;
    this.currencyID = this.priceData.currency_id;
    this.countryID = this.priceData.country_id;

    if (!this.price && this.pricePcs) {
      this.priceType = 'box';
    }
  },
  methods: {
    handleClose () {
      this.$bus.$emit('shipper-edit-price-popup-close');
    },
    validateForm () {
      FormUtils.validate(this.$refs['shipper-edit-price-form'], this.savePrice);
    },
    savePrice () {
      let dataToSend = {
        shipper_id: this.shipperID,
        currency_id: this.currencyID,
        country_id: this.countryID
      };

      if (this.priceType === 'flat') {
        dataToSend.price = this.price;
        dataToSend.price_pcs = 0;
        dataToSend.price_first_box = 0;
        dataToSend.price_second_box = 0;
        dataToSend.price_next_box = 0;
      } else {
        dataToSend.price = 0;
        dataToSend.price_pcs = this.pricePcs;
        dataToSend.price_first_box = this.priceFirstBox;
        dataToSend.price_second_box = this.priceSecondBox;
        dataToSend.price_next_box = this.priceNextBox;
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/shippers/prices/update/' + this.id,
        formData: dataToSend,
        successTitle: this.$t('price.savePriceSuccessTitle'),
        successText: this.$t('price.savePriceSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('price.savePriceErrorText'),
        successAction: () => {
          this.$bus.$emit('shipper-reload-prices');
          this.handleClose();
        }
      });
    },
    cancel () {
      this.handleClose();
    },
    setPriceType (newType) {
      Vue.set(this, 'priceType', newType);
    }
  }
}
</script>
